import _ from 'underscore';
import { EffectChoiceKey } from '@bingads-webui-campaign/media-common-utils';
import { stringifySync, parseSync } from '@bingads-webui/safe-json';
import { extractInfoForSerialization, extractInfoForDeserialization } from './validate-schema';
import { EffectSettingsAlias } from './image-metadata-schema';

const convertStateToMetadata = ({
  effectSettings,
} = {}) => {
  const {
    effectKey,
    /* Should not save URL related stuff since it may expire anytime. */
    // effectSrc,
    effectOptions = {},
  } = effectSettings || {};
  const processEffect = effectKey && effectKey !== EffectChoiceKey.None;

  if (!processEffect) {
    return null;
  }

  const metaObject = extractInfoForSerialization({
    obj: {
      effectSettings: {
        effectKey,
        effectOptions,
      },
    },
    effectKey,
  });

  return metaObject;
};

/*
 * Attention: this is serialized and saved in db,
 * so need to consider backward compatibility when upgrading.
 */
export const buildImageAssetMetadataToSave = (assetModel) => {
  const metaObject = {};

  const placementMeta = convertStateToMetadata(assetModel);

  if (!_.isEmpty(placementMeta)) {
    _.extend(metaObject, placementMeta);
  }

  return metaObject;
};

// metadata is coupled with image content.
// if we have same images with different metadata, this is not supported by MT asset entity.
export const serializeImageAssetMetadata = (assetModel, isBulkUpsert = true, isInlineAssetEditorRefreshEnabled = false) => {
  if (_.isEmpty(assetModel)) {
    return '';
  }

  // isInlineAssetEditorRefreshEnabled, adapt to old version metadata.
  // previous metadata should be compatible with new version, but previous metadata has only one effect
  let metaObject = buildImageAssetMetadataToSave(assetModel);

  if (_.isEmpty(metaObject)) {
    return '';
  }

  if (isInlineAssetEditorRefreshEnabled && _.isObject(assetModel)) {
    const effectSettings = _.result(assetModel, 'effectSettings', {});
    const effectOptions = _.result(effectSettings, 'effectOptions', {});

    if (effectOptions.designerEffect === EffectChoiceKey.None
      && effectOptions.backgroundBlur == null && effectOptions.backgroundColor == null) {
      return '';
    }

    const combinedEffect = [];
    if (effectOptions.designerEffect) {
      const designerEffect = buildImageAssetMetadataToSave({
        effectSettings: {
          effectKey: EffectChoiceKey.DesignerEffects,
          effectOptions: {
            designerEffect: effectOptions.designerEffect,
          },
        },
      });
      combinedEffect.push(designerEffect);
    }

    if (effectOptions.backgroundBlur) {
      const blurEffect = buildImageAssetMetadataToSave({
        effectSettings: {
          effectKey: EffectChoiceKey.BackgroundBlur,
          effectOptions: {
            backgroundBlur: effectOptions.backgroundBlur,
          },
        },
      });
      combinedEffect.push(blurEffect);
    }

    // Adapt to the old single effect
    if (effectOptions.backgroundColor) {
      const solidColorEffect = buildImageAssetMetadataToSave({
        effectSettings: {
          effectKey: EffectChoiceKey.BackgroundSolidColor,
          effectOptions: {
            backgroundColor: effectOptions.backgroundColor,
          },
        },
      });
      combinedEffect.push(solidColorEffect);
    }

    if (combinedEffect.length > 0) {
      metaObject = {
        effect: combinedEffect[0].effect,
        combinedEffect,
      };
    }
  }

  const serialized = stringifySync(metaObject);

  return isBulkUpsert
    ? serialized.replace(/,/g, encodeURIComponent(',')) // Comma is reserved in bulk upsert call
    : serialized;
};

const revertMetaDataInner = (metaData) => {
  const combinedEffectArray = _.isArray(metaData.combinedEffect) ? metaData.combinedEffect :
    _.keys(metaData.combinedEffect).map(key => metaData.combinedEffect[key]);

  if (combinedEffectArray.length > 0) {
    const filterEffect = combinedEffectArray.find(effect => _.result(effect, ['effect', 'key']) === EffectChoiceKey.DesignerEffects);
    const blurEffect = combinedEffectArray.find(effect => _.result(effect, ['effect', 'key']) === EffectChoiceKey.BackgroundBlur);
    const colorEffect = combinedEffectArray.find(effect => _.result(effect, ['effect', 'key']) === EffectChoiceKey.BackgroundSolidColor);
    return {
      filter: filterEffect ? extractInfoForDeserialization({ obj: filterEffect }) : null,
      blur: blurEffect ? extractInfoForDeserialization({ obj: blurEffect }) : null,
      color: colorEffect ? extractInfoForDeserialization({ obj: colorEffect }) : null,
    };
  }
  return {
    filter: null,
    blur: null,
    color: null,
  };
};

export const deserializeImageAssetMetadata = (metaString, isInlineAssetEditorRefreshEnabled = false) => {
  if (metaString) {
    const decoded = metaString.replace(new RegExp(encodeURIComponent(','), 'g'), ',');
    const metaObj = parseSync(decoded);

    let metaObjCopy = metaObj;
    if (metaObj && metaObj[EffectSettingsAlias]) {
      metaObjCopy = { [EffectSettingsAlias]: metaObj[EffectSettingsAlias] };
    }

    if (!_.isEmpty(metaObj)) {
      let model = extractInfoForDeserialization({ obj: metaObjCopy });

      // isInlineAssetEditorRefreshEnabled, adapt to old version metadata.
      // previous metadata should be compatible with new version, but previous metadata has only one effect
      if (isInlineAssetEditorRefreshEnabled && metaObj.combinedEffect) {
        const { filter, blur, color } = revertMetaDataInner(metaObj);

        const filterValue = _.result(filter, ['effectSettings', 'effectOptions', 'designerEffect'], null);
        const blurValue = _.result(blur, ['effectSettings', 'effectOptions', 'backgroundBlur'], null);
        const colorValue = _.result(color, ['effectSettings', 'effectOptions', 'backgroundColor'], null);

        if (filter === null && blurValue === null && colorValue === null) {
          return model;
        }

        const effectOptions = {};
        let effectKey = EffectChoiceKey.None;

        if (colorValue) {
          effectKey = EffectChoiceKey.BackgroundSolidColor;
          effectOptions.backgroundColor = colorValue;
        }

        if (blurValue) {
          effectKey = EffectChoiceKey.BackgroundBlur;
          effectOptions.backgroundBlur = blurValue;
        }

        if (filterValue) {
          // When we have two style its effectKey will must be DesignerEffects
          effectKey = EffectChoiceKey.DesignerEffects;
          effectOptions.designerEffect = filterValue;
        }

        model = {
          effectSettings: {
            effectKey,
            effectOptions,
          },
        };
      }

      return model;
    }
  }

  return null;
};
